import React, { useState, useEffect } from 'react';
import '../styling/forms.css';

const AnesthesiaForm = ({ anesthesiaData = {}, handleAnesthesiaChange, handleFormSubmission }) => {
  const [showIntraPostIssues, setShowIntraPostIssues] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleIntraPostIssuesChange = (e) => {
    const isChecked = e.target.checked;
    setShowIntraPostIssues(isChecked);
    handleAnesthesiaChange({
      target: {
        name: 'intraPostOperativeEvents',
        value: isChecked,
      },
    });
  };

  const intraPostOpSection = [
    {
      title: 'Critical Events',
      name: 'intraPostCriticalEvents',
      issues: ['Death', 'Cardiac arrest', 'Unplanned ICU admission (or equivalent)']
    },
    {
      title: 'Airway/Pulmonary Events',
      name: 'intraPostAirwayPulmonaryEvents',
      issues: ['Respiratory arrest', 'Pulmonary edema', 'Bronchospasm required treatment (Rx)', 'Aspiration', 'Laryngospasm required succinylcholine treatment (Rx)', 'Intubation/Reintubation (unplanned)', 'Inability to secure airway', 'Pneumothorax', 'Unanticipated difficult airway', 'Laryngospasm required succinylcholine treatment (Rx)', 'Retained throat pack (oral surgery)']
    },
    {
      title: 'Cardiac Events',
      name: 'intraPostCardiacEvents', 
      issues: ['Myocardial ischemia, indicated by clinical symptoms (Sx), ST segment changes or echocardiography', 'New premature ventricular contractions (PVCs), bradycardia, atrial fibrillation, or other dysrhythmias required unanticipated treatment (Rx)', 'Hypotension required unanticipated treatment (Rx) with a continuous pressor infusion', 'Myocardial infarction']
    },
    {
      title: 'Medication/Transfusion Events',
      name: 'intraPostMedicationTransfusionEvents',
      issues: ['Medication error', 'Anaphylaxis', 'Unanticipated medication reaction', 'Unanticipated transfusion', 'Use of sedation/narcotic reversal treatment (Rx)', 'Transfusion reaction', 'Malignant Hyperthermia', 'Local anesthetic systemic toxicity']
    },
    {
      title: 'Neurological/Other Events',
      name: 'intraPostNeurologicalOtherEvents',
      issues: ['Seizure', 'New stroke', 'Peripheral neurologic deficit', 'Uncontrolled blood sugar required treatment (Rx)', 'Position injury', 'Fall from chair/OR table']
    },
    {
      title: 'Equipment/Environment Events',
      name: 'intraPostEquipmentEnvironmentEvents',
      issues: ['Equipment problem', 'Surgical fire', 'Burn injury']
    },
    {
      title: 'Process Events',
      name: 'intraPostProcessEvents',
      issues: ['Unplanned outpatient admission', 'Emergency/Altered workflow', 'Unplanned hospital admission', 'Unplanned return to OR', 'Case canceled (includes pre-OR)', 'Incorrect patient', 'Incorrect site or procedure']
    },
    {
      title: 'Other Events',
      name: 'intraPostOtherEvents',
      issues: ['Incorrect tooth removed']
    }
  ];

  const medications = ['Propofol', 'Midazolam', 'Ketamine', 'Fentanyl/Remifentanil', 'Dexmedetomidine', 'Narcan', 'Flumazenil'];

  const renderCheckboxGroup = (section, data) => (
    <div className="form-section" key={section.name}>
      <label>{section.title}:</label>
      <div className="checkbox-group">
        {section.issues.map((issue) => (
          <label key={issue}>
            <input
              type="checkbox"
              name={section.name}
              value={issue}
              checked={data[section.name] ? data[section.name].includes(issue) : false}
              onChange={(e) => handleAnesthesiaChange({
                target: {
                  name: section.name,
                  value: e.target.checked
                    ? [...(data[section.name] || []), issue]
                    : (data[section.name] || []).filter((item) => item !== issue),
                },
              })}
            />
            {issue}
          </label>
        ))}
      </div>
    </div>
  );

  const renderMedicationDoseGroup = () => (
    <div className="form-section">
    <label>Medication and Dose Administered:</label>
    <div className="medication-dose-group">
      {medications.map((medication) => (
        <div key={medication} className="medication-dose">
          <label>
            <input
              type="checkbox"
              name="medications"
              value={medication}
              checked={anesthesiaData.medications ? anesthesiaData.medications.includes(medication) : false}
              onChange={(e) =>
                handleAnesthesiaChange({
                  target: {
                    name: 'medications',
                    value: e.target.checked
                      ? [...(anesthesiaData.medications || []), medication]
                      : (anesthesiaData.medications || []).filter((item) => item !== medication),
                  },
                })
              }
            />
            {medication}
          </label>
          {anesthesiaData.medications && anesthesiaData.medications.includes(medication) && (
            <input
              type="text"
              placeholder="Dose"
              name={`${medication}Dose`}
              value={anesthesiaData[`${medication}Dose`] || ''}
              onChange={handleAnesthesiaChange}
            />
          )}
        </div>
      ))}
      <div className="medication-dose">
        <label>
          <input
            type="checkbox"
            name="medications"
            value="Other"
            checked={anesthesiaData.medications ? anesthesiaData.medications.includes("Other") : false}
            onChange={(e) =>
              handleAnesthesiaChange({
                target: {
                  name: 'medications',
                  value: e.target.checked
                    ? [...(anesthesiaData.medications || []), "Other"]
                    : (anesthesiaData.medications || []).filter((item) => item !== "Other"),
                },
              })
            }
          />
          Other
        </label>
        {anesthesiaData.medications && anesthesiaData.medications.includes("Other") && (
          <>
            <input
              type="text"
              placeholder="Enter Medication"
              name="otherMedication"
              value={anesthesiaData.otherMedication || ''}
              onChange={handleAnesthesiaChange}
            />
            <input
              type="text"
              placeholder="Dose"
              name="otherDose"
              value={anesthesiaData.otherDose || ''}
              onChange={handleAnesthesiaChange}
            />
          </>
        )}
      </div>
    </div>
  </div>
  );

  return (
    <div className="fill-form">
      <h1>Fill out the Anesthesia Form</h1>
      <form onSubmit={handleFormSubmission}>
        <div className="form-section">
          <label>
            Age (years):
            <input
              type="number"
              name="age"
              value={anesthesiaData.age}
              onChange={handleAnesthesiaChange}
              required
            />
          </label>
          <label>
            Weight (lb):
            <input
              type="number"
              name="weight"
              value={anesthesiaData.weight}
              onChange={handleAnesthesiaChange}
              required
            />
          </label>
          <label>
            Date of Surgery:
            <input
              type="date"
              name="dateOfSurgery"
              value={anesthesiaData.dateOfSurgery}
              onChange={handleAnesthesiaChange}
              required
            />
          </label>
        </div>
        <div className="form-section">
          <label>Sex:</label>
          <div className="checkbox-group">
            <label>
              <input
                type="radio"
                name="sex"
                value="Male"
                checked={anesthesiaData.sex === 'Male'}
                onChange={(e) => handleAnesthesiaChange({
                  target: { name: 'sex', value: e.target.checked ? 'Male' : '' },
                })}
              />
              Male
            </label>
            <label>
              <input
                type="radio"
                name="sex"
                value="Female"
                checked={anesthesiaData.sex === 'Female'}
                onChange={(e) => handleAnesthesiaChange({
                  target: { name: 'sex', value: e.target.checked ? 'Female' : '' },
                })}
              />
              Female
            </label>
          </div>
        </div>
        <div className="form-section">
          <label>American Society of Anesthesiologists Classification:</label>
          <div className="checkbox-group">
            {['I', 'II', 'III', 'IV', 'E'].map((grade) => (
              <label key={grade}>
                <input
                  type="checkbox"
                  name="americanSocietyAnesthesiologistsClassification"
                  value={grade}
                  checked={anesthesiaData.americanSocietyAnesthesiologistsClassification === grade}
                  onChange={(e) => handleAnesthesiaChange({
                    target: { name: 'americanSocietyAnesthesiologistsClassification', value: e.target.checked ? grade : '' },
                  })}
                />
                {grade}
              </label>
            ))}
          </div>
        </div>
        <div className="form-section">
          <label>Anesthesia Type:</label>
          <div className="checkbox-group">
            {['General', 'Local', 'Nitrous Oxide'].map((type) => (
              <label key={type}>
                <input
                  type="checkbox"
                  name="anesthesiaType"
                  value={type}
                  checked={anesthesiaData.anesthesiaType === type}
                  onChange={(e) => handleAnesthesiaChange({
                    target: { name: 'anesthesiaType', value: e.target.checked ? type : '' },
                  })}
                />
                {type}
              </label>
            ))}
          </div>
        </div>
        <div className="form-section">
          <label>Anesthesia Provider:</label>
          <div className="checkbox-group">
            {['Surgeon', 'Anesthesiologist', 'Certified Registered Nurse Anesthetist'].map((provider) => (
              <label key={provider}>
                <input
                  type="checkbox"
                  name="anesthesiaProvider"
                  value={provider}
                  checked={anesthesiaData.anesthesiaProvider === provider}
                  onChange={(e) => handleAnesthesiaChange({
                    target: { name: 'anesthesiaProvider', value: e.target.checked ? provider : '' },
                  })}
                />
                {provider}
              </label>
            ))}
          </div>
        </div>
        <div className="form-section">
          <label>Surgical Time (minutes):
            <input
              type="number"
              name="surgicalTime"
              value={anesthesiaData.surgicalTime}
              onChange={handleAnesthesiaChange}
              required
            />
          </label>
        </div>
        <div className="form-section">
          <label>
            Primary procedure:
            <input
              type="text"
              name="primaryProcedure"
              value={anesthesiaData.primaryProcedure}
              onChange={handleAnesthesiaChange}
            />
          </label>
        </div>
        <div className="form-section">
          <label>Co-Morbidities:</label>
          <div className="checkbox-group">
            {['Upper Respiratory Infection', 'Asthma', 'Smoker', 'Snorer/Obstructive Sleep Apnea', 'Hypertension', 'Cardiovascular Disease', 'Diabetes Mellitus', 'Developmental Delay', 'Malnourished', 'Other'].map(condition => (
              <label key={condition}>
                <input
                  type="checkbox"
                  name="coMorbidities"
                  value={condition}
                  checked={anesthesiaData.coMorbidities.includes(condition)}
                  onChange={(e) => handleAnesthesiaChange({
                    target: {
                      name: 'coMorbidities',
                      value: e.target.checked 
                        ? [...anesthesiaData.coMorbidities, condition]
                        : anesthesiaData.coMorbidities.filter(item => item !== condition)
                    }
                  })}
                />
                {condition}
              </label>
            ))}
            <input
              type="text"
              name="coMorbiditiesOther"
              placeholder="Specify other co-morbidities"
              value={anesthesiaData.coMorbiditiesOther || ''}
              onChange={handleAnesthesiaChange}
            />
          </div>
        </div>
        <div className="form-section">
          <label>
            Were there any intraoperative or post operative events?
            <input
              type="checkbox"
              name="intraPostOperativeEvents"
              checked={showIntraPostIssues}
              onChange={handleIntraPostIssuesChange}
            />
          </label>
          {showIntraPostIssues && intraPostOpSection.map((intraPostOpSection) => renderCheckboxGroup(intraPostOpSection, anesthesiaData))}
        </div>
        {renderMedicationDoseGroup()}
        <button type="submit" className="anesthesia-submit-btn">Submit Form</button>
      </form>
    </div>
  );
};

export default AnesthesiaForm;
